import { z } from 'zod';
/**
 * Per `conversation-audit-log` capability — schema for the audit
 * pipeline that captures every conversation turn (text or voice) into
 * a queryable store.
 *
 * Two shapes:
 *   - `AuditTurn` — one DDB row, one half of a turn (user OR assistant).
 *     Stored in `nx-audit-turns-{env}` with PK sessionId + SK tsTurnKey.
 *   - `AuditSessionSummary` — admin-side aggregation, derived by
 *     grouping turns server-side. Not persisted — built per-query.
 *
 * The `rawBlobKey` field points at the corresponding S3 object under
 * `audit-logs/<characterId>/<sessionId>/<turnId>.json` when the row
 * carries a raw upstream response; empty string when no blob (user
 * turns never have blobs; some provider paths skip it).
 */
export const AuditMediumEnum = z.enum(['text', 'voice']);
export const AuditRoleEnum = z.enum(['user', 'assistant']);
export const AuditSurfaceEnum = z.enum(['web', 'extension', 'workflow', 'operator']);
export const AuditTurnSchema = z.object({
    sessionId: z.string().min(1),
    /** `<turnEpochMs>#<turnUuid>` — deterministic chronological sort. */
    tsTurnKey: z.string().min(1),
    characterId: z.string().min(1),
    /** Empty string when the caller didn't send `x-nx-visitor` header. */
    visitorId: z.string(),
    /**
     * Per `audit-mode-visitor-enrichment` change — which entry path
     * produced this turn:
     *   - 'fan':       caller has a valid Fan Cognito JWT → visitorId is
     *                  the fan's Cognito sub; admin UI looks up email +
     *                  display name from the fans table.
     *   - 'anonymous': customer-service embed flow, no fan identity;
     *                  visitorId is the random x-nx-visitor cookie.
     *
     * Optional for back-compat — rows written before this change have
     * no value and are displayed as 客服模式 by default in the UI.
     */
    visitorKind: z.enum(['fan', 'anonymous']).optional(),
    /** Client surface that produced the turn. Optional for legacy rows. */
    surface: AuditSurfaceEnum.optional(),
    role: AuditRoleEnum,
    /** The textual content — chat message, ASR transcript, or TTS-spoken text. */
    text: z.string(),
    medium: AuditMediumEnum,
    /** Provider name when known; empty for user-side rows. */
    provider: z.string().default(''),
    /** Provider model id when known. */
    model: z.string().default(''),
    promptTokens: z.number().int().nonnegative().default(0),
    completionTokens: z.number().int().nonnegative().default(0),
    /** S3 key for the full upstream response; empty when no blob. */
    rawBlobKey: z.string().default(''),
    createdAt: z.string().datetime(),
});
/**
 * Admin-side aggregation. Built by the list endpoint by grouping
 * `AuditTurn` rows by sessionId; not persisted.
 */
export const AuditSessionSummarySchema = z.object({
    sessionId: z.string().min(1),
    characterId: z.string().min(1),
    visitorId: z.string(),
    /** From the first turn of the session. Old rows without a value
     *  default to 'anonymous' on the admin display. */
    visitorKind: z.enum(['fan', 'anonymous']).optional(),
    /** Surface inferred from the session turns. */
    surface: AuditSurfaceEnum.optional(),
    /** Enriched server-side when visitorKind==='fan' — looked up from
     *  the fans table by visitorId. Empty for anonymous rows. */
    visitorEmail: z.string().optional(),
    visitorDisplayName: z.string().optional(),
    firstSeenAt: z.string().datetime(),
    lastSeenAt: z.string().datetime(),
    turnCount: z.number().int().positive(),
    /** `'mixed'` when the session has both text + voice turns. */
    medium: z.enum(['text', 'voice', 'mixed']),
    /** Last 80 chars of the most recent USER-role turn — preview for the list table. */
    lastUserText: z.string(),
});
